import React, { useCallback, useEffect, useRef, useState } from "react";
import QuestionSingleRadio from "./QuestionSingleRadio";
import QuestionLabel from "./QuestionLabel";
import Question from "./Question";
import { IAssuranceSanteInitialState } from "@/lib/feature/assuranceSante/slice";
import QuestionDateInput from "./QuestionDateInput";
import helpers from "@/utils/helpers";
import { useTranslation } from "react-i18next";

interface IQuestionAdherents {
  type: string;
  value?: IAssuranceSanteInitialState["data"]["adherent"];
  onSelect: (value: IAssuranceSanteInitialState["data"]["adherent"]) => any;
}

export const TypeEnum = Object.freeze({
  Single: "you",
  Couple: "you and your partner",
  SingleWithChild: "you and your kids",
  CoupleWithChild: "you, your partner and your kids",
});

const QuestionAdherent = ({ value, onSelect, type }: IQuestionAdherents) => {
  const { t } = useTranslation();

  const divRef = useRef<HTMLDivElement>(null);

  const [yourGender, setYourGender] = useState<string>(
    value?.find((adhr) => adhr.type === "main")?.civility ?? ""
  );
  const [partnerGender, setPartnerGender] = useState<string>(
    value?.find((adhr) => adhr.type === "partner")?.civility ?? ""
  );
  const [childrens, setChildrens] = useState<
    IAssuranceSanteInitialState["data"]["adherent"]
  >(value?.filter((adhr) => adhr.type === "children") ?? []);
  const [internalAdherents, setInternalAdherents] = useState<
    IAssuranceSanteInitialState["data"]["adherent"]
  >(value ?? []);

  const handleAddAdult = (
    birthDate: string,
    civility: string,
    adultType: "main" | "partner"
  ) => {
    let newArray = [...internalAdherents];

    const existingIndex = newArray.findIndex(
      (item) => item.civility === civility
    );

    if (existingIndex !== -1) {
      newArray[existingIndex] = {
        ...newArray[existingIndex],
        dob: birthDate,
        type: adultType,
        year: birthDate.split("-")[0],
      };
    } else {
      newArray.push({
        dob: birthDate,
        type: adultType,
        civility: civility,
        year: birthDate.split("-")[0],
      });
    }

    setInternalAdherents(newArray);

    if (
      ((type === TypeEnum.CoupleWithChild && partnerGender) ||
        (type === TypeEnum.SingleWithChild && yourGender)) &&
      internalAdherents.filter((adhr) => adhr.type === "children").length <=
        0
    )
      handleAddEmptyChildren();
  };

  const handleAddChildren = (birthDate: string, itemIndex: number) => {
    let updatedChildrens = [...childrens];

    if (updatedChildrens[itemIndex]) {
      updatedChildrens[itemIndex] = {
        ...updatedChildrens[itemIndex],
        dob: birthDate,
        type: "children",
        year: birthDate.split("-")[0],
      };
    } else {
      updatedChildrens.push({
        dob: birthDate,
        type: "children",
        year: birthDate.split("-")[0],
      });
    }

    setChildrens(updatedChildrens);
  };

  const handleAddEmptyChildren = () => {
    setChildrens([...childrens, { dob: "", type: "children", year: "" }]);
  };

  const handleRemoveChildren = (index: number) => {
    const updatedChildrens = [...childrens];
    updatedChildrens.splice(index, 1);
    setChildrens(updatedChildrens);
  };

  const areAllValuesFilled = (obj: any[]) => {
    for (const key in obj) {
      if (
        obj.hasOwnProperty(key) &&
        (!obj[key] || !obj[key].dob || !obj[key].year)
      ) {
        return false;
      }
    }
    return true;
  };

  const childrensAreValid = () => {
    return childrens.length > 0 && areAllValuesFilled(childrens);
  };

  const handleDecideSelection = () => {
    onSelect([...internalAdherents, ...childrens]);
  };

  useEffect(() => {
    const childrenElements = Array.from(divRef.current?.children || []);
    const lastElement = childrenElements[childrenElements.length - 1];
    const nextSibling = childrenElements[0].nextElementSibling;

    // remove class from remaining elements
    childrenElements.forEach((element, index) => {
      element.classList.remove("md:min-h-[calc(100dvh-250px)]");
      element.classList.remove("mt-10");
      element.removeAttribute("id");
    });

    if (lastElement && !nextSibling) {
      lastElement.classList.add("md:min-h-[calc(100dvh-250px)]");
      lastElement.classList.add("mt-10");
      lastElement.setAttribute("id", "adherant-editing");
      lastElement.scrollIntoView({ behavior: "smooth" });
    }
  }, [internalAdherents]);

  useEffect(() => {
    if (value?.length! <= 0) {
      setYourGender("");
      setPartnerGender("");
      setInternalAdherents([]);
      setChildrens([]);
    }

    return () => {};
  }, [value]);

  useEffect(() => {
    if (
      (type === TypeEnum.Couple && internalAdherents.length === 2) ||
      (type === TypeEnum.Single && internalAdherents.length === 1)
    )
      handleDecideSelection();

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalAdherents, type]);

  return (
    <div className="flex flex-col gap-10" ref={divRef}>
      <Question className="gap-4">
        <Question>
          <QuestionLabel title={t("AssuranceSante.adherent.key1")} />
          <QuestionSingleRadio
            value={yourGender}
            options={[
              {
                label: t("AssuranceSante.adherent.key2"),
                value: "male",
                appendIcon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 28 28"
                  >
                    <path
                      stroke="#082623"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M20.062 15.568a7.698 7.698 0 1 1-15.396 0 7.698 7.698 0 0 1 15.396 0Z"
                      clipRule="evenodd"
                    ></path>
                    <path
                      stroke="#082623"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="m23.333 4.736-5.457 5.458m5.444-.584.01-4.876-4.877.01"
                    ></path>
                  </svg>
                ),
              },
              {
                label: t("AssuranceSante.adherent.key3"),
                value: "female",
                appendIcon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    fill="none"
                    viewBox="0 0 28 28"
                  >
                    <path
                      stroke="#082623"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M7.206 12.801a8.064 8.064 0 1 1 16.128 0 8.064 8.064 0 0 1-16.128 0Z"
                      clipRule="evenodd"
                    ></path>
                    <path
                      stroke="#082623"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="m4.667 23.262 4.832-4.832m-4.713 1.288 3.423 3.424"
                    ></path>
                  </svg>
                ),
              },
            ]}
            onSelectOption={(value) => {
              setYourGender(value as string);
            }}
          />
        </Question>
        {yourGender && (
          <Question>
            <QuestionLabel title={t("AssuranceSante.adherent.key4")} />
            <QuestionDateInput
              value={value?.find((adhr) => adhr.type === "main")?.dob ?? ""}
              errorMessage={t("AssuranceSante.adherent.key5")}
              onSelect={(date) => {
                handleAddAdult(date, "male", "main");
              }}
            />
          </Question>
        )}
      </Question>

      {type !== TypeEnum.Single &&
        type !== TypeEnum.SingleWithChild &&
        internalAdherents.findIndex((item) => item.type === "main") > -1 && (
          <Question className="gap-4">
            <Question>
              <QuestionLabel title={t("AssuranceSante.adherent.key6")} />
              <QuestionSingleRadio
                value={partnerGender}
                options={[
                  {
                    label: t("AssuranceSante.adherent.key2"),
                    value: "male",
                    appendIcon: (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        fill="none"
                        viewBox="0 0 28 28"
                      >
                        <path
                          stroke="#082623"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M20.062 15.568a7.698 7.698 0 1 1-15.396 0 7.698 7.698 0 0 1 15.396 0Z"
                          clipRule="evenodd"
                        ></path>
                        <path
                          stroke="#082623"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="m23.333 4.736-5.457 5.458m5.444-.584.01-4.876-4.877.01"
                        ></path>
                      </svg>
                    ),
                  },
                  {
                    label: t("AssuranceSante.adherent.key3"),
                    value: "female",
                    appendIcon: (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="28"
                        height="28"
                        fill="none"
                        viewBox="0 0 28 28"
                      >
                        <path
                          stroke="#082623"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M7.206 12.801a8.064 8.064 0 1 1 16.128 0 8.064 8.064 0 0 1-16.128 0Z"
                          clipRule="evenodd"
                        ></path>
                        <path
                          stroke="#082623"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="m4.667 23.262 4.832-4.832m-4.713 1.288 3.423 3.424"
                        ></path>
                      </svg>
                    ),
                  },
                ]}
                onSelectOption={(value) => {
                  setPartnerGender(value as string);
                }}
              />
            </Question>
            {partnerGender && (
              <Question>
                <QuestionLabel title={t("AssuranceSante.adherent.key7")} />
                <QuestionDateInput
                  value={
                    value?.find((adhr) => adhr.type === "partner")?.dob ?? ""
                  }
                  errorMessage={t("AssuranceSante.adherent.key5")}
                  onSelect={(date) => {
                    handleAddAdult(date, partnerGender, "partner");
                  }}
                />
              </Question>
            )}
          </Question>
        )}

      {childrens.length > 0 && (
        <React.Fragment>
          <Question className="gap-4">
            {childrens.map((children, index) => (
              <Question key={index}>
                <QuestionLabel title={t("AssuranceSante.adherent.key8")} />
                <QuestionDateInput
                  value={children.dob}
                  onSelect={(date) => {
                    handleAddChildren(date, index);
                  }}
                  errorMessage={t("AssuranceSante.adherent.key5")}
                />
                {childrens.length > 1 && index > 0 && (
                  <button
                    onClick={() => handleRemoveChildren(index)}
                    className="mr-auto text-red-500 hover:underline mt-4"
                  >
                    {childrens[index].dob
                      ? t("AssuranceSante.adherent.key11")
                      : t("AssuranceSante.adherent.key12")}
                  </button>
                )}
              </Question>
            ))}
          </Question>

          {childrensAreValid() && (
            <div className="flex flex-col justify-between gap-4 md:flex-row">
              <button
                onClick={handleAddEmptyChildren}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[56px] text-base font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-center text-secondary bg-[#3B82F626] hover:bg-[#3B82F644] h-[56px] px-[26px] w-fit"
              >
                {t("AssuranceSante.adherent.key9")}
              </button>
              <button
                disabled={childrens.length <= 0}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[56px] text-base font-semibold ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-center bg-thirdy text-white hover:bg-thirdy/90 h-[56px] px-[26px] w-fit"
                onClick={handleDecideSelection}
              >
                {t("AssuranceSante.adherent.key10")}
              </button>
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default QuestionAdherent;
